import { FC } from 'react';
import { Button } from '@mui/material';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { DoubleChargeInvoiceLineDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, formatMoney } from 'src/utils/utility';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import IconWarningRed from 'src/assets/images/icons/danger-red.svg';

export type DoubleChargeDecisions = Record<string, boolean | undefined>;

const buttonBase = {
  minWidth: 0,
  gap: '10px',
  padding: '4px 12px',
  borderRadius: '4px',
  fontSize: '10px',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
  '&:active': { boxShadow: 'none' },
} as const;

const unfilledAcceptStyle = {
  ...buttonBase,
  backgroundColor: '#EBEBEB',
  color: '#9F9F9F',
  border: '1px solid #EBEBEB',
  '&:hover': { backgroundColor: '#E0F3E6', color: '#00992B', border: '1px solid #00992B', boxShadow: 'none' },
} as const;

const unfilledRejectStyle = {
  ...buttonBase,
  backgroundColor: '#EBEBEB',
  color: '#9F9F9F',
  border: '1px solid #EBEBEB',
  '&:hover': { backgroundColor: '#FCE4E9', color: '#E72547', border: '1px solid #E72547', boxShadow: 'none' },
} as const;

const acceptActiveStyle = {
  ...buttonBase,
  backgroundColor: '#00992B',
  color: '#fff',
  border: 'none',
  '&:hover': { backgroundColor: '#00992B', boxShadow: 'none' },
} as const;

const rejectActiveStyle = {
  ...buttonBase,
  backgroundColor: '#E72547',
  color: '#fff',
  border: '1px solid #E72547',
  '&:hover': { backgroundColor: '#E72547', boxShadow: 'none' },
} as const;

const acceptHoverStyle = {
  ...buttonBase,
  backgroundColor: '#EBEBEB',
  color: '#9F9F9F',
  border: '1px solid #EBEBEB',
  '&:hover': { backgroundColor: '#E0F3E6', color: '#00992B', border: '1px solid #00992B', boxShadow: 'none' },
} as const;

const rejectHoverStyle = {
  ...buttonBase,
  backgroundColor: '#EBEBEB',
  color: '#9F9F9F',
  border: '1px solid #EBEBEB',
  '&:hover': { backgroundColor: '#FCE4E9', color: '#E72547', border: '1px solid #E72547', boxShadow: 'none' },
} as const;

export interface IDoubleChargeResolutionTableProps {
  charges: DoubleChargeInvoiceLineDto[];
  decisions: DoubleChargeDecisions;
  onDecisionChange: (decisions: DoubleChargeDecisions) => void;
  disabled?: boolean;
  showValidationError?: boolean;
}

export const DoubleChargeResolutionTable: FC<IDoubleChargeResolutionTableProps> = ({
  charges,
  decisions,
  onDecisionChange,
  disabled = false,
  showValidationError = false,
}) => {
  const handleDecision = (key: string, accepted: boolean) => {
    onDecisionChange({ ...decisions, [key]: accepted });
  };

  const hasUnfilled = charges.some((line) => {
    const key = line.deviationId ?? line.chargeId!;
    return decisions[key] === undefined;
  });

  const columns: MRT_ColumnDef<DoubleChargeInvoiceLineDto>[] = [
    {
      accessorKey: 'chargeName',
      header: 'Charge Name',
      Cell: ({ row }) => (
        <div>
          <div className='text-14 font-medium text-neutral-80'>{beautifyEnum(row.original.chargeName) || '—'}</div>
          <div className='text-12 font-medium' style={{ color: '#28a745' }}>
            {row.original.invoiceNumber ? `Inv No. ${row.original.invoiceNumber}` : 'Current Invoice'}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'deviationType',
      header: 'Deviation Type',
      Cell: ({ row }) => <span className='text-14 text-neutral-70'>{beautifyEnum(row.original.deviationType) || '—'}</span>,
    },
    {
      accessorKey: 'unitPrice',
      header: 'Unit Price',
      Cell: ({ row }) => <span className='text-14 text-neutral-70'>{formatMoney(row.original.unitPrice, row.original.currency)}</span>,
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      Cell: ({ row }) => <span className='text-14 text-neutral-70'>{row.original.quantity ?? '—'}</span>,
    },
    {
      accessorKey: 'decision',
      header: 'Decision',
      muiTableBodyCellProps: { sx: { borderBottom: '1px solid #DFDFDF!important' } },
      Cell: ({ row }) => {
        const key = row.original.deviationId ?? row.original.chargeId!;
        const decision = decisions[key];

        if (disabled) {
          if (decision === true)
            return (
              <span className='text-14 font-medium' style={{ color: '#00992B' }}>
                Accepted
              </span>
            );
          if (decision === false)
            return (
              <span className='text-14 font-medium' style={{ color: '#E72547' }}>
                Rejected
              </span>
            );
          return <span className='text-14 font-medium text-neutral-40'>Unfilled</span>;
        }

        return (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              size='small'
              variant='contained'
              disableElevation
              onClick={() => handleDecision(key, true)}
              sx={decision === true ? acceptActiveStyle : decision === false ? acceptHoverStyle : unfilledAcceptStyle}
            >
              Accept
            </Button>
            <Button
              size='small'
              variant='contained'
              disableElevation
              onClick={() => handleDecision(key, false)}
              sx={decision === false ? rejectActiveStyle : decision === true ? rejectHoverStyle : unfilledRejectStyle}
            >
              Reject
            </Button>
          </div>
        );
      },
    },
  ];

  const table = useMaterialReactTable({
    data: charges,
    columns,
    enableRowNumbers: false,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
  });

  return (
    <div>
      <CustomTable table={table} />
      {showValidationError && hasUnfilled && (
        <div className='flex items-center gap-4 mt-8'>
          <img src={IconWarningRed} alt='' className='w-12 h-12' />
          <span className='text-12' style={{ color: '#E72547' }}>
            You need to fill the decision
          </span>
        </div>
      )}
    </div>
  );
};
